import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import PhoneInput from "./PhoneInput";
import ErrorBanner from "./ErrorBanner";
import ConfirmationModal from "./ConfirmationModal";
import { validateEmail, validatePhone } from "../shared/utils/validation";
import GenderSelect from "./GenderSelect";
import BirthMonthYearInput from "./BirthMonthYearInput";
import { getGenderDisplayLabel, validateRequiredGender } from "../shared/utils/genderOptions";
import {
  EDUCATION_LEVEL_OPTIONS,
  HEAR_ABOUT_US_OPTIONS,
  HEAR_ABOUT_US_OTHER_VALUE,
  getEducationLevelLabel,
  getHearAboutUsDisplayLabel,
  mapEducationLevelValue,
  mapHearAboutUsValue,
  validateRequiredEducationLevel,
  validateRequiredHearAboutUs,
  validateRequiredUsZip,
} from "../shared/utils/parentProfileOptions";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { displayEmailOrDash } from "../shared/utils/displayEmail";
import { SMS_OPT_IN_SOURCES } from "../shared/utils/smsOptIn";
import SmsOptInConsentBlock from "./SmsOptInConsentBlock";
import "./ProfileInfoModal.css";
import {
  formatBirthMonthYearDisplay,
  getBirthMonthYearInputValue,
  normalizeBirthMonthYearForSave,
} from "../shared/utils/birthMonthYear";
import { GRADE_OPTIONS, getGradeLabel, mapGradeValue } from "../shared/utils/gradeOptions";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

function birthdayForApi(raw) {
  const normalized = normalizeBirthMonthYearForSave(raw);
  return normalized.storageYmd;
}

function normalizeTrim(v) {
  return v != null ? String(v).trim() : "";
}

function parseParentName(name) {
  const parts = normalizeTrim(name).split(/\s+/).filter(Boolean);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") || "" };
}

/**
 * Editable profile modal (single page): role-based sections.
 * - Orange summary card for account contact + nested edit; youth & school on the same card when Participant.
 * - Parent / guardian: card + nested edit for participants.
 */
export default function ProfileInfoEditorTabs({ titleId, titleAction, hidePageChrome = false }) {
  const { userInfo, setUserInfo, hasRole } = useAuth();
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id ? String(userInfo._id) : "";

  const isParent = hasRole("Parent");
  const isParticipant = hasRole("Participant");

  const [bannerError, setBannerError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", mode: "alert", onConfirm: null });

  /** Parent-only: edit contact in nested modal. */
  const [parentContactEditorOpen, setParentContactEditorOpen] = useState(false);
  const [contactDraft, setContactDraft] = useState(null);
  const contactDraftBaselineRef = useRef("");
  /** Participant: parent/guardian in nested modal (same card + edit pattern as parent account contact). */
  const [guardianEditorOpen, setGuardianEditorOpen] = useState(false);
  const [guardianDraft, setGuardianDraft] = useState(null);
  const guardianDraftBaselineRef = useRef("");
  /** Non–parent account: contact (+ youth & school when Participant) in one card and nested editor. */
  const [participantContactEditorOpen, setParticipantContactEditorOpen] = useState(false);
  /** Contact fields plus, when `isParticipant`, birthday / gender / grade / school. */
  const [participantContactDraft, setParticipantContactDraft] = useState(null);
  const participantContactDraftBaselineRef = useRef("");

  const [contact, setContact] = useState({ first_name: "", last_name: "", email_id: "", phone_number: "" });
  const [youth, setYouth] = useState({ birthday: "", gender: "", grade: "", school: "" });
  const [parentProfile, setParentProfile] = useState({
    preferred_name: "",
    gender: "",
    zip_code: "",
    highest_education_level: "",
    hear_about_us: "",
    hear_about_us_other: "",
  });
  const [guardian, setGuardian] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  useEffect(() => {
    if (!userInfo) return;
    setBannerError("");
    setSaving(false);
    setConfirmDialog({ open: false, title: "", message: "", mode: "alert", onConfirm: null });
    setContact({
      first_name: userInfo?.first_name ?? "",
      last_name: userInfo?.last_name ?? "",
      email_id: userInfo?.email_id ?? "",
      phone_number: userInfo?.phone_number ?? "",
    });
    setYouth({
      birthday: getBirthMonthYearInputValue(userInfo?.birthday),
      gender: userInfo?.gender ?? "",
      grade: mapGradeValue(userInfo?.grade ?? ""),
      school: userInfo?.school ?? "",
    });
    setParentProfile({
      preferred_name: userInfo?.preferred_name ?? "",
      gender: userInfo?.gender ?? "",
      zip_code: userInfo?.zip_code ?? "",
      highest_education_level: mapEducationLevelValue(userInfo?.highest_education_level ?? ""),
      hear_about_us: mapHearAboutUsValue(userInfo?.hear_about_us ?? ""),
      hear_about_us_other: userInfo?.hear_about_us_other ?? "",
    });
    const parsed = parseParentName(userInfo?.parent_name || "");
    setGuardian({
      first_name: parsed.first,
      last_name: parsed.last,
      email: userInfo?.parent_email ?? "",
      phone: userInfo?.parent_phone ?? "",
    });
    setParentContactEditorOpen(false);
    setContactDraft(null);
    contactDraftBaselineRef.current = "";
    setGuardianEditorOpen(false);
    setGuardianDraft(null);
    guardianDraftBaselineRef.current = "";
    setParticipantContactEditorOpen(false);
    setParticipantContactDraft(null);
    participantContactDraftBaselineRef.current = "";
  }, [userInfo]);

  const contactDraftDirty = useMemo(() => {
    if (!parentContactEditorOpen || !contactDraft) return false;
    const base = contactDraftBaselineRef.current || "";
    try {
      return JSON.stringify(contactDraft) !== base;
    } catch {
      return true;
    }
  }, [parentContactEditorOpen, contactDraft]);

  const guardianDraftDirty = useMemo(() => {
    if (!guardianEditorOpen || !guardianDraft) return false;
    const base = guardianDraftBaselineRef.current || "";
    try {
      return JSON.stringify(guardianDraft) !== base;
    } catch {
      return true;
    }
  }, [guardianEditorOpen, guardianDraft]);

  const participantContactDraftDirty = useMemo(() => {
    if (!participantContactEditorOpen || !participantContactDraft) return false;
    const base = participantContactDraftBaselineRef.current || "";
    try {
      return JSON.stringify(participantContactDraft) !== base;
    } catch {
      return true;
    }
  }, [participantContactEditorOpen, participantContactDraft]);

  function closeParentContactEditorModal() {
    setParentContactEditorOpen(false);
    setContactDraft(null);
    contactDraftBaselineRef.current = "";
  }

  function closeGuardianEditorModal() {
    setGuardianEditorOpen(false);
    setGuardianDraft(null);
    guardianDraftBaselineRef.current = "";
  }

  function closeParticipantContactEditorModal() {
    setParticipantContactEditorOpen(false);
    setParticipantContactDraft(null);
    participantContactDraftBaselineRef.current = "";
  }

  function resetContactYouthFromUserInfo() {
    if (!userInfo) return;
    setContact({
      first_name: userInfo?.first_name ?? "",
      last_name: userInfo?.last_name ?? "",
      email_id: userInfo?.email_id ?? "",
      phone_number: userInfo?.phone_number ?? "",
    });
    setYouth({
      birthday: getBirthMonthYearInputValue(userInfo?.birthday),
      gender: userInfo?.gender ?? "",
      grade: mapGradeValue(userInfo?.grade ?? ""),
      school: userInfo?.school ?? "",
    });
    setParentProfile({
      preferred_name: userInfo?.preferred_name ?? "",
      gender: userInfo?.gender ?? "",
      zip_code: userInfo?.zip_code ?? "",
      highest_education_level: mapEducationLevelValue(userInfo?.highest_education_level ?? ""),
      hear_about_us: mapHearAboutUsValue(userInfo?.hear_about_us ?? ""),
      hear_about_us_other: userInfo?.hear_about_us_other ?? "",
    });
  }

  function validateParentProfileRow(row, { requireGender }) {
    if (!row) return "Missing profile details.";
    if (requireGender) {
      const genderErr = validateRequiredGender(row.gender);
      if (genderErr) return genderErr;
    }
    const zipErr = validateRequiredUsZip(row.zip_code);
    if (zipErr) return zipErr;
    const eduErr = validateRequiredEducationLevel(row.highest_education_level);
    if (eduErr) return eduErr;
    const hearErr = validateRequiredHearAboutUs(row.hear_about_us, row.hear_about_us_other);
    if (hearErr) return hearErr;
    return "";
  }

  function resetGuardianFromUserInfo() {
    if (!userInfo) return;
    const parsed = parseParentName(userInfo?.parent_name || "");
    setGuardian({
      first_name: parsed.first,
      last_name: parsed.last,
      email: userInfo?.parent_email ?? "",
      phone: userInfo?.parent_phone ?? "",
    });
  }

  async function updateUserPatch(_id, patch) {
    const resp = await apiFetch(serverUrl + "/updateUser", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: String(_id), ...patch }),
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => "");
      let msg = (t || "").trim() || "Save failed";
      try {
        const j = JSON.parse(t);
        if (j && typeof j.message === "string" && j.message.trim()) msg = j.message.trim();
      } catch {
        /* keep msg as body text */
      }
      throw new Error(msg);
    }
    return true;
  }

  function validateContactRow(row) {
    if (!row) return "Missing contact.";
    const first = normalizeTrim(row.first_name);
    const last = normalizeTrim(row.last_name);
    const email = normalizeTrim(row.email_id);
    const phone = normalizeTrim(row.phone_number);
    if (!first) return "First name is required.";
    if (!last) return "Last name is required.";
    const e = validateEmail(email);
    if (!e.valid) return e.message || "Invalid email.";
    const p = validatePhone(phone);
    if (!p.valid) return p.message || "Invalid phone.";
    return "";
  }

  function beginEditParentContact() {
    if (!isParent) return;
    setBannerError("");
    closeGuardianEditorModal();
    closeParticipantContactEditorModal();
    const draft = {
      ...contact,
      ...parentProfile,
      sms_opt_in: userInfo?.sms_opt_in === true,
      ...(isParticipant
        ? {
            birthday: youth.birthday,
            gender: youth.gender,
            grade: youth.grade,
            school: youth.school,
          }
        : {}),
    };
    setContactDraft(draft);
    try {
      contactDraftBaselineRef.current = JSON.stringify(draft);
    } catch {
      contactDraftBaselineRef.current = "";
    }
    setParentContactEditorOpen(true);
  }

  async function saveParentContactFromModal() {
    if (!isParent || !contactDraft) return false;
    const err = validateContactRow(contactDraft);
    if (err) {
      setConfirmDialog({ open: true, title: "Check details", message: err, mode: "alert" });
      return false;
    }
    if (isParticipant) {
      const genderErr = validateRequiredGender(contactDraft.gender);
      if (genderErr) {
        setConfirmDialog({ open: true, title: "Check details", message: genderErr, mode: "alert" });
        return false;
      }
    }
    const profileErr = validateParentProfileRow(contactDraft, { requireGender: !isParticipant });
    if (profileErr) {
      setConfirmDialog({ open: true, title: "Check details", message: profileErr, mode: "alert" });
      return false;
    }
    setSaving(true);
    setBannerError("");
    try {
      const patch = {
        first_name: normalizeTrim(contactDraft.first_name),
        last_name: normalizeTrim(contactDraft.last_name),
        phone_number: normalizeTrim(contactDraft.phone_number),
        sms_opt_in: !!contactDraft.sms_opt_in,
        sms_opt_in_source: SMS_OPT_IN_SOURCES.PROFILE,
      };
      if (isParticipant) {
        patch.birthday = birthdayForApi(contactDraft.birthday);
        patch.gender = normalizeTrim(contactDraft.gender);
        patch.grade = mapGradeValue(contactDraft.grade || "");
        patch.school = normalizeTrim(contactDraft.school);
      } else {
        patch.gender = normalizeTrim(contactDraft.gender);
      }
      patch.preferred_name = normalizeTrim(contactDraft.preferred_name);
      patch.zip_code = normalizeTrim(contactDraft.zip_code);
      patch.highest_education_level = mapEducationLevelValue(contactDraft.highest_education_level || "");
      patch.hear_about_us = mapHearAboutUsValue(contactDraft.hear_about_us || "");
      patch.hear_about_us_other =
        mapHearAboutUsValue(contactDraft.hear_about_us || "") === HEAR_ABOUT_US_OTHER_VALUE
          ? normalizeTrim(contactDraft.hear_about_us_other)
          : "";
      await updateUserPatch(user_id, patch);
      setContact({
        first_name: patch.first_name,
        last_name: patch.last_name,
        email_id: normalizeTrim(contact.email_id),
        phone_number: patch.phone_number,
      });
      if (isParticipant) {
        setYouth({
          birthday: getBirthMonthYearInputValue(patch.birthday),
          gender: patch.gender,
          grade: mapGradeValue(patch.grade || ""),
          school: patch.school,
        });
      }
      setParentProfile({
        preferred_name: patch.preferred_name,
        gender: isParticipant ? patch.gender : patch.gender,
        zip_code: patch.zip_code,
        highest_education_level: mapEducationLevelValue(patch.highest_education_level || ""),
        hear_about_us: mapHearAboutUsValue(patch.hear_about_us || ""),
        hear_about_us_other: patch.hear_about_us_other,
      });
      setUserInfo((prev) => (prev ? { ...prev, ...patch } : prev));
      setConfirmDialog({ open: true, title: "Saved", message: "Profile updated.", mode: "alert" });
      return true;
    } catch (e) {
      setBannerError(getMessageForFetchError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleParentContactEditorSave() {
    const ok = await saveParentContactFromModal();
    if (ok) closeParentContactEditorModal();
  }

  function beginEditParticipantContact() {
    if (isParent) return;
    setBannerError("");
    closeParentContactEditorModal();
    closeGuardianEditorModal();
    resetGuardianFromUserInfo();
    const draft = isParticipant
      ? {
          ...contact,
          preferred_name: userInfo?.preferred_name ?? "",
          birthday: youth.birthday,
          gender: youth.gender,
          grade: youth.grade,
          school: youth.school,
          sms_opt_in: userInfo?.sms_opt_in === true,
        }
      : { ...contact, sms_opt_in: userInfo?.sms_opt_in === true };
    setParticipantContactDraft(draft);
    try {
      participantContactDraftBaselineRef.current = JSON.stringify(draft);
    } catch {
      participantContactDraftBaselineRef.current = "";
    }
    setParticipantContactEditorOpen(true);
  }

  async function saveParticipantContactFromModal() {
    if (isParent || !participantContactDraft) return false;
    const err = validateContactRow(participantContactDraft);
    if (err) {
      setConfirmDialog({ open: true, title: "Check details", message: err, mode: "alert" });
      return false;
    }
    if (isParticipant) {
      const genderErr = validateRequiredGender(participantContactDraft.gender);
      if (genderErr) {
        setConfirmDialog({ open: true, title: "Check details", message: genderErr, mode: "alert" });
        return false;
      }
    }
    setSaving(true);
    setBannerError("");
    try {
      const patch = {
        first_name: normalizeTrim(participantContactDraft.first_name),
        last_name: normalizeTrim(participantContactDraft.last_name),
        phone_number: normalizeTrim(participantContactDraft.phone_number),
        sms_opt_in: !!participantContactDraft.sms_opt_in,
        sms_opt_in_source: SMS_OPT_IN_SOURCES.PROFILE,
      };
      if (isParticipant) {
        patch.birthday = birthdayForApi(participantContactDraft.birthday);
        patch.gender = normalizeTrim(participantContactDraft.gender);
        patch.grade = mapGradeValue(participantContactDraft.grade || "");
        patch.school = normalizeTrim(participantContactDraft.school);
        patch.preferred_name = normalizeTrim(participantContactDraft.preferred_name);
      }
      await updateUserPatch(user_id, patch);
      setContact({
        first_name: patch.first_name,
        last_name: patch.last_name,
        email_id: normalizeTrim(contact.email_id),
        phone_number: patch.phone_number,
      });
      if (isParticipant) {
        setYouth({
          birthday: getBirthMonthYearInputValue(patch.birthday),
          gender: patch.gender,
          grade: mapGradeValue(patch.grade || ""),
          school: patch.school,
        });
        setParentProfile((prev) => ({
          ...prev,
          preferred_name: patch.preferred_name,
        }));
      }
      setUserInfo((prev) => (prev ? { ...prev, ...patch } : prev));
      setConfirmDialog({ open: true, title: "Saved", message: "Profile updated.", mode: "alert" });
      return true;
    } catch (e) {
      setBannerError(getMessageForFetchError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleParticipantContactEditorSave() {
    const ok = await saveParticipantContactFromModal();
    if (ok) closeParticipantContactEditorModal();
  }

  function validateGuardianRow(row) {
    if (!row) return "Missing parent/guardian.";
    const first = normalizeTrim(row.first_name);
    const last = normalizeTrim(row.last_name);
    const email = normalizeTrim(row.email);
    const phone = normalizeTrim(row.phone);
    if (!first || !last || !email || !phone) {
      return "Parent/guardian name, email, and phone are required.";
    }
    const eCheck = validateEmail(email);
    if (!eCheck.valid) return eCheck.message || "Invalid parent email.";
    const pCheck = validatePhone(phone);
    if (!pCheck.valid) return pCheck.message || "Invalid parent phone.";
    return "";
  }

  function beginEditGuardian() {
    if (!isParticipant) return;
    setBannerError("");
    closeParentContactEditorModal();
    closeParticipantContactEditorModal();
    resetContactYouthFromUserInfo();
    const draft = { ...guardian };
    setGuardianDraft(draft);
    try {
      guardianDraftBaselineRef.current = JSON.stringify(draft);
    } catch {
      guardianDraftBaselineRef.current = "";
    }
    setGuardianEditorOpen(true);
  }

  async function persistGuardianRow(row) {
    const err = validateGuardianRow(row);
    if (err) {
      setConfirmDialog({ open: true, title: "Check details", message: err, mode: "alert" });
      return false;
    }
    const first = normalizeTrim(row.first_name);
    const last = normalizeTrim(row.last_name);
    const email = normalizeTrim(row.email);
    const phone = normalizeTrim(row.phone);
    setSaving(true);
    setBannerError("");
    try {
      const resp = await apiFetch(serverUrl + "/saveYouthParentContact", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          user_id,
          parent: { first_name: first, last_name: last, email, phone },
        }),
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        throw new Error(t || "Unable to save parent/guardian contact.");
      }
      setGuardian({ first_name: first, last_name: last, email, phone });
      setUserInfo((prev) =>
        prev
          ? {
              ...prev,
              parent_name: `${first} ${last}`.trim(),
              parent_email: email,
              parent_phone: phone,
            }
          : prev,
      );
      setConfirmDialog({ open: true, title: "Saved", message: "Parent/guardian contact updated.", mode: "alert" });
      return true;
    } catch (e) {
      setBannerError(getMessageForFetchError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleGuardianEditorSave() {
    if (!guardianDraft) return;
    const ok = await persistGuardianRow(guardianDraft);
    if (ok) closeGuardianEditorModal();
  }

  const profileEditorsLocked =
    saving ||
    parentContactEditorOpen ||
    guardianEditorOpen ||
    participantContactEditorOpen;

  function renderAccountContactCard() {
    const displayName = [normalizeTrim(contact.first_name), normalizeTrim(contact.last_name)].filter(Boolean).join(" ") || "—";
    const email = displayEmailOrDash(contact.email_id);
    const phoneDisp = formatPhoneDisplay(contact.phone_number);
    const onEditContact = isParent ? beginEditParentContact : beginEditParticipantContact;
    return (
      <div className="cards-wrapper ProfileInfoModal-linked-children-cards" style={{ marginTop: 0 }}>
        <div className="card" style={{ borderLeftColor: "#ea580c" }}>
          <div className="card-header">
            <div className="card-header-content">
              <div className="card-title">{displayName}</div>
            </div>
            <div className="card-header-actions">
              <button type="button" className="btn btn-primary-outline btn-sm" disabled={profileEditorsLocked} onClick={onEditContact}>
                Edit
              </button>
            </div>
          </div>
          <div className="card-meta-row">
            <span>
              {email} · {phoneDisp || "—"}
            </span>
          </div>
          {isParticipant ? (
            <>
              <div className="card-meta-row">
                <span>
                  Date of birth {formatBirthMonthYearDisplay(youth.birthday)} · {getGenderDisplayLabel(youth.gender)} · {getGradeLabel(youth.grade)}
                </span>
              </div>
              <div className="card-meta-row">
                <span>{normalizeTrim(youth.school) || "—"}</span>
              </div>
            </>
          ) : null}
          {isParent ? (
            <>
              {normalizeTrim(parentProfile.preferred_name) ? (
                <div className="card-meta-row">
                  <span>Preferred name {normalizeTrim(parentProfile.preferred_name)}</span>
                </div>
              ) : null}
              {!isParticipant ? (
                <div className="card-meta-row">
                  <span>{getGenderDisplayLabel(parentProfile.gender)}</span>
                </div>
              ) : null}
              <div className="card-meta-row">
                <span>
                  {normalizeTrim(parentProfile.zip_code) || "—"} · {getEducationLevelLabel(parentProfile.highest_education_level)}
                </span>
              </div>
              <div className="card-meta-row">
                <span>{getHearAboutUsDisplayLabel(parentProfile.hear_about_us, parentProfile.hear_about_us_other)}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  function renderGuardianCard() {
    const displayName = [normalizeTrim(guardian.first_name), normalizeTrim(guardian.last_name)].filter(Boolean).join(" ") || "—";
    const email = displayEmailOrDash(guardian.email);
    const phoneDisp = formatPhoneDisplay(guardian.phone);
    return (
      <div className="cards-wrapper ProfileInfoModal-linked-children-cards" style={{ marginTop: 0 }}>
        <div className="card" style={{ borderLeftColor: "#2563eb" }}>
          <div className="card-header">
            <div className="card-header-content">
              <div className="card-title">{displayName}</div>
            </div>
            <div className="card-header-actions">
              <button type="button" className="btn btn-primary-outline btn-sm" disabled={profileEditorsLocked} onClick={beginEditGuardian}>
                Edit
              </button>
            </div>
          </div>
          <div className="card-meta-row">
            <span>
              {email} · {phoneDisp || "—"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title || "Message"}
        message={confirmDialog.message || ""}
        mode={confirmDialog.mode || "alert"}
        onConfirm={() => {
          const cb = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", mode: "alert", onConfirm: null });
          if (typeof cb === "function") cb();
        }}
        onCancel={() => setConfirmDialog({ open: false, title: "", message: "", mode: "alert", onConfirm: null })}
      />

      {parentContactEditorOpen && contactDraft ? (
        <div className="ProfileInfoModal-overlay ProfileInfoModal-overlay--nested" role="dialog" aria-modal="true" aria-labelledby="pi-parent-contact-title">
          <div className="ProfileInfoModal-content" onClick={(e) => e.stopPropagation()}>
            <div className="panel-title" id="pi-parent-contact-title" style={{ marginBottom: 12 }}>
              {isParticipant ? "Edit contact & school" : "Edit contact"}
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-parent-contact-fn">First name *</label>
                  <input
                    id="pi-parent-contact-fn"
                    className="input"
                    disabled={saving}
                    value={contactDraft.first_name}
                    onChange={(e) => setContactDraft((p) => (p ? { ...p, first_name: e.target.value } : p))}
                  />
                </div>
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-parent-contact-ln">Last name *</label>
                  <input
                    id="pi-parent-contact-ln"
                    className="input"
                    disabled={saving}
                    value={contactDraft.last_name}
                    onChange={(e) => setContactDraft((p) => (p ? { ...p, last_name: e.target.value } : p))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-parent-profile-pn">Preferred name</label>
                  <input
                    id="pi-parent-profile-pn"
                    className="input"
                    disabled={saving}
                    value={contactDraft.preferred_name ?? ""}
                    onChange={(e) => setContactDraft((p) => (p ? { ...p, preferred_name: e.target.value } : p))}
                  />
                </div>
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-parent-contact-em">Email *</label>
                  <input
                    id="pi-parent-contact-em"
                    className="input"
                    type="email"
                    disabled
                    readOnly
                    value={contactDraft.email_id}
                    title="Email can only be changed by an admin via Swap email in User Admin"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-parent-contact-ph">Phone *</label>
                  <PhoneInput
                    id="pi-parent-contact-ph"
                    className="input"
                    disabled={saving}
                    value={contactDraft.phone_number}
                    onChange={(v) => setContactDraft((p) => (p ? { ...p, phone_number: v } : p))}
                  />
                </div>
                {isParticipant ? (
                  <div className="form-field" style={{ minWidth: 0 }}>
                    <label htmlFor="pi-parent-youth-bd">Date of birth</label>
                    <BirthMonthYearInput
                      id="pi-parent-youth-bd"
                      className="input"
                      disabled={saving}
                      value={contactDraft.birthday ?? ""}
                      onChange={(next) => setContactDraft((p) => (p ? { ...p, birthday: next } : p))}
                    />
                  </div>
                ) : (
                  <div className="form-field" style={{ minWidth: 0 }}>
                    <label htmlFor="pi-parent-profile-gn">Gender *</label>
                    <GenderSelect
                      id="pi-parent-profile-gn"
                      disabled={saving}
                      value={contactDraft.gender ?? ""}
                      onChange={(e) => setContactDraft((p) => (p ? { ...p, gender: e.target.value } : p))}
                      required
                    />
                  </div>
                )}
              </div>
              <SmsOptInConsentBlock
                checkboxId="pi-parent-sms-opt-in"
                checked={!!contactDraft.sms_opt_in}
                disabled={saving}
                onChange={(next) => setContactDraft((p) => (p ? { ...p, sms_opt_in: next } : p))}
              />
              {isParticipant ? (
                <>
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-parent-youth-gn">Gender *</label>
                      <GenderSelect
                        id="pi-parent-youth-gn"
                        disabled={saving}
                        value={contactDraft.gender ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, gender: e.target.value } : p))}
                        required
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-parent-youth-gr">Grade</label>
                      <select
                        id="pi-parent-youth-gr"
                        className="input select"
                        disabled={saving}
                        value={contactDraft.grade ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, grade: e.target.value } : p))}
                      >
                        <option value="">Select…</option>
                        {GRADE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-parent-youth-sc">School</label>
                      <input
                        id="pi-parent-youth-sc"
                        className="input"
                        disabled={saving}
                        value={contactDraft.school ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, school: e.target.value } : p))}
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-parent-profile-zip">Zip code *</label>
                      <input
                        id="pi-parent-profile-zip"
                        className="input"
                        disabled={saving}
                        value={contactDraft.zip_code ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, zip_code: e.target.value } : p))}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        required
                      />
                    </div>
                  </div>
                  {mapHearAboutUsValue(contactDraft.hear_about_us) === HEAR_ABOUT_US_OTHER_VALUE ? (
                    <div className="form-row">
                      <div className="form-field" style={{ minWidth: 0 }}>
                        <label htmlFor="pi-parent-profile-hear">How did you hear about us? *</label>
                        <select
                          id="pi-parent-profile-hear"
                          className="input select"
                          disabled={saving}
                          value={contactDraft.hear_about_us ?? ""}
                          onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us: e.target.value } : p))}
                          required
                        >
                          <option value="">Select…</option>
                          {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field" style={{ minWidth: 0 }}>
                        <label htmlFor="pi-parent-profile-hear-other">Please specify *</label>
                        <input
                          id="pi-parent-profile-hear-other"
                          className="input"
                          disabled={saving}
                          value={contactDraft.hear_about_us_other ?? ""}
                          onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us_other: e.target.value } : p))}
                          required
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="form-row">
                      <div className="form-field form-field--full">
                        <label htmlFor="pi-parent-profile-hear">How did you hear about us? *</label>
                        <select
                          id="pi-parent-profile-hear"
                          className="input select"
                          disabled={saving}
                          value={contactDraft.hear_about_us ?? ""}
                          onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us: e.target.value } : p))}
                          required
                        >
                          <option value="">Select…</option>
                          {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-field form-field--full">
                      <label htmlFor="pi-parent-profile-edu">Highest education level completed *</label>
                      <select
                        id="pi-parent-profile-edu"
                        className="input select"
                        disabled={saving}
                        value={contactDraft.highest_education_level ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, highest_education_level: e.target.value } : p))}
                        required
                      >
                        <option value="">Select…</option>
                        {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {mapHearAboutUsValue(contactDraft.hear_about_us) === HEAR_ABOUT_US_OTHER_VALUE ? (
                    <>
                      <div className="form-row">
                        <div className="form-field form-field--full">
                          <label htmlFor="pi-parent-profile-zip">Zip code *</label>
                          <input
                            id="pi-parent-profile-zip"
                            className="input"
                            disabled={saving}
                            value={contactDraft.zip_code ?? ""}
                            onChange={(e) => setContactDraft((p) => (p ? { ...p, zip_code: e.target.value } : p))}
                            inputMode="numeric"
                            autoComplete="postal-code"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-field" style={{ minWidth: 0 }}>
                          <label htmlFor="pi-parent-profile-hear">How did you hear about us? *</label>
                          <select
                            id="pi-parent-profile-hear"
                            className="input select"
                            disabled={saving}
                            value={contactDraft.hear_about_us ?? ""}
                            onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us: e.target.value } : p))}
                            required
                          >
                            <option value="">Select…</option>
                            {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-field" style={{ minWidth: 0 }}>
                          <label htmlFor="pi-parent-profile-hear-other">Please specify *</label>
                          <input
                            id="pi-parent-profile-hear-other"
                            className="input"
                            disabled={saving}
                            value={contactDraft.hear_about_us_other ?? ""}
                            onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us_other: e.target.value } : p))}
                            required
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="form-row">
                      <div className="form-field" style={{ minWidth: 0 }}>
                        <label htmlFor="pi-parent-profile-zip">Zip code *</label>
                        <input
                          id="pi-parent-profile-zip"
                          className="input"
                          disabled={saving}
                          value={contactDraft.zip_code ?? ""}
                          onChange={(e) => setContactDraft((p) => (p ? { ...p, zip_code: e.target.value } : p))}
                          inputMode="numeric"
                          autoComplete="postal-code"
                          required
                        />
                      </div>
                      <div className="form-field" style={{ minWidth: 0 }}>
                        <label htmlFor="pi-parent-profile-hear">How did you hear about us? *</label>
                        <select
                          id="pi-parent-profile-hear"
                          className="input select"
                          disabled={saving}
                          value={contactDraft.hear_about_us ?? ""}
                          onChange={(e) => setContactDraft((p) => (p ? { ...p, hear_about_us: e.target.value } : p))}
                          required
                        >
                          <option value="">Select…</option>
                          {HEAR_ABOUT_US_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-field form-field--full">
                      <label htmlFor="pi-parent-profile-edu">Highest education level completed *</label>
                      <select
                        id="pi-parent-profile-edu"
                        className="input select"
                        disabled={saving}
                        value={contactDraft.highest_education_level ?? ""}
                        onChange={(e) => setContactDraft((p) => (p ? { ...p, highest_education_level: e.target.value } : p))}
                        required
                      >
                        <option value="">Select…</option>
                        {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-primary-outline btn-sm" disabled={saving} onClick={closeParentContactEditorModal}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={saving || !contactDraftDirty} onClick={handleParentContactEditorSave}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {guardianEditorOpen && guardianDraft ? (
        <div className="ProfileInfoModal-overlay ProfileInfoModal-overlay--nested" role="dialog" aria-modal="true" aria-labelledby="pi-guardian-editor-title">
          <div className="ProfileInfoModal-content" onClick={(e) => e.stopPropagation()}>
            <div className="panel-title" id="pi-guardian-editor-title" style={{ marginBottom: 12 }}>
              Edit parent / guardian
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-guardian-edit-fn">First name *</label>
                  <input
                    id="pi-guardian-edit-fn"
                    className="input"
                    disabled={saving}
                    value={guardianDraft.first_name}
                    onChange={(e) => setGuardianDraft((p) => (p ? { ...p, first_name: e.target.value } : p))}
                  />
                </div>
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-guardian-edit-ln">Last name *</label>
                  <input
                    id="pi-guardian-edit-ln"
                    className="input"
                    disabled={saving}
                    value={guardianDraft.last_name}
                    onChange={(e) => setGuardianDraft((p) => (p ? { ...p, last_name: e.target.value } : p))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-guardian-edit-em">Email *</label>
                  <input
                    id="pi-guardian-edit-em"
                    className="input"
                    type="email"
                    disabled={saving}
                    value={guardianDraft.email}
                    onChange={(e) => setGuardianDraft((p) => (p ? { ...p, email: e.target.value } : p))}
                  />
                </div>
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-guardian-edit-ph">Phone *</label>
                  <PhoneInput
                    id="pi-guardian-edit-ph"
                    className="input"
                    disabled={saving}
                    value={guardianDraft.phone}
                    onChange={(v) => setGuardianDraft((p) => (p ? { ...p, phone: v } : p))}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-primary-outline btn-sm" disabled={saving} onClick={closeGuardianEditorModal}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={saving || !guardianDraftDirty} onClick={handleGuardianEditorSave}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {participantContactEditorOpen && participantContactDraft ? (
        <div className="ProfileInfoModal-overlay ProfileInfoModal-overlay--nested" role="dialog" aria-modal="true" aria-labelledby="pi-self-contact-title">
          <div className="ProfileInfoModal-content" onClick={(e) => e.stopPropagation()}>
            <div className="panel-title" id="pi-self-contact-title" style={{ marginBottom: 12 }}>
              {isParticipant ? "Edit contact & school" : "Edit contact"}
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-self-contact-fn">First name *</label>
                  <input
                    id="pi-self-contact-fn"
                    className="input"
                    disabled={saving}
                    value={participantContactDraft.first_name}
                    onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, first_name: e.target.value } : p))}
                  />
                </div>
                <div className="form-field" style={{ minWidth: 0 }}>
                  <label htmlFor="pi-self-contact-ln">Last name *</label>
                  <input
                    id="pi-self-contact-ln"
                    className="input"
                    disabled={saving}
                    value={participantContactDraft.last_name}
                    onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, last_name: e.target.value } : p))}
                  />
                </div>
              </div>
              {isParticipant ? (
                <>
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-profile-pn">Preferred name</label>
                      <input
                        id="pi-self-profile-pn"
                        className="input"
                        disabled={saving}
                        value={participantContactDraft.preferred_name ?? ""}
                        onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, preferred_name: e.target.value } : p))}
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-contact-em">Email *</label>
                      <input
                        id="pi-self-contact-em"
                        className="input"
                        type="email"
                        disabled
                        readOnly
                        value={participantContactDraft.email_id}
                        title="Email can only be changed by an admin via Swap email in User Admin"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-contact-ph">Phone *</label>
                      <PhoneInput
                        id="pi-self-contact-ph"
                        className="input"
                        disabled={saving}
                        value={participantContactDraft.phone_number}
                        onChange={(v) => setParticipantContactDraft((p) => (p ? { ...p, phone_number: v } : p))}
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-youth-bd">Date of birth</label>
                      <BirthMonthYearInput
                        id="pi-self-youth-bd"
                        className="input"
                        disabled={saving}
                        value={participantContactDraft.birthday ?? ""}
                        onChange={(next) => setParticipantContactDraft((p) => (p ? { ...p, birthday: next } : p))}
                      />
                    </div>
                  </div>
                  <SmsOptInConsentBlock
                    checkboxId="pi-self-sms-opt-in"
                    checked={!!participantContactDraft.sms_opt_in}
                    disabled={saving}
                    onChange={(next) => setParticipantContactDraft((p) => (p ? { ...p, sms_opt_in: next } : p))}
                  />
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-youth-gn">Gender *</label>
                      <GenderSelect
                        id="pi-self-youth-gn"
                        disabled={saving}
                        value={participantContactDraft.gender ?? ""}
                        onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, gender: e.target.value } : p))}
                        required
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-youth-gr">Grade</label>
                      <select
                        id="pi-self-youth-gr"
                        className="input select"
                        disabled={saving}
                        value={participantContactDraft.grade ?? ""}
                        onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, grade: e.target.value } : p))}
                      >
                        <option value="">Select…</option>
                        {GRADE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field form-field--full">
                      <label htmlFor="pi-self-youth-sc">School</label>
                      <input
                        id="pi-self-youth-sc"
                        className="input"
                        disabled={saving}
                        value={participantContactDraft.school ?? ""}
                        onChange={(e) => setParticipantContactDraft((p) => (p ? { ...p, school: e.target.value } : p))}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-contact-em">Email *</label>
                      <input
                        id="pi-self-contact-em"
                        className="input"
                        type="email"
                        disabled
                        readOnly
                        value={participantContactDraft.email_id}
                        title="Email can only be changed by an admin via Swap email in User Admin"
                      />
                    </div>
                    <div className="form-field" style={{ minWidth: 0 }}>
                      <label htmlFor="pi-self-contact-ph">Phone *</label>
                      <PhoneInput
                        id="pi-self-contact-ph"
                        className="input"
                        disabled={saving}
                        value={participantContactDraft.phone_number}
                        onChange={(v) => setParticipantContactDraft((p) => (p ? { ...p, phone_number: v } : p))}
                      />
                    </div>
                  </div>
                  <SmsOptInConsentBlock
                    checkboxId="pi-staff-sms-opt-in"
                    checked={!!participantContactDraft.sms_opt_in}
                    disabled={saving}
                    onChange={(next) => setParticipantContactDraft((p) => (p ? { ...p, sms_opt_in: next } : p))}
                  />
                </>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-primary-outline btn-sm" disabled={saving} onClick={closeParticipantContactEditorModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={saving || !participantContactDraftDirty}
                onClick={handleParticipantContactEditorSave}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!hidePageChrome ? (
        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280", marginBottom: "2px" }}>
            Account
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", minWidth: 0 }}>
            <h2 id={titleId} style={{ margin: 0, fontSize: "1.15rem", fontWeight: 600, color: "#111827", minWidth: 0 }}>
              Profile info
            </h2>
            {titleAction}
          </div>
        </div>
      ) : null}

      {bannerError ? <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} /> : null}

      {!userInfo ? (
        <div style={{ fontWeight: 600 }}>Loading…</div>
      ) : (
        <>
          <div className="modal-form">{renderAccountContactCard()}</div>

          {isParticipant ? (
            <div className="modal-form" style={{ marginTop: 16 }}>
              <div className="panel-title" style={{ marginBottom: 6 }}>
                Parent / Guardian
              </div>
              {renderGuardianCard()}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

